import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Shield, ArrowLeft, ExternalLink, Users, Vault } from "lucide-react";

export default function AccessPortal() {
  const navigate = useNavigate();
  const [providerVaults, setProviderVaults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProviderVaults();
  }, []);

  const loadProviderVaults = async () => {
    try {
      const response = await axios.get(`${API}/vault/access`);
      setProviderVaults(response.data);
    } catch (error) {
      toast.error('Failed to load provider vaults');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Shield className="w-12 h-12 text-indigo-600 mx-auto mb-4 animate-pulse" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const groupItemsByCategory = (items) => {
    return items.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {});
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Access Portal</h1>
                <p className="text-sm text-slate-600">View vaults you have access to</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {providerVaults.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-300" data-testid="no-access">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Access Granted</h3>
              <p className="text-slate-600">You haven't been added as a dependent by anyone yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {providerVaults.map((providerData, idx) => {
              const groupedItems = groupItemsByCategory(providerData.vault_items);
              
              return (
                <Card key={idx} className="border-slate-200 shadow-sm" data-testid={`provider-vault-${idx}`}>
                  <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-xl">
                        {providerData.provider.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl">{providerData.provider.name}'s Vault</CardTitle>
                        <CardDescription className="mt-1">{providerData.provider.email}</CardDescription>
                        <p className="text-xs text-slate-600 mt-2">
                          {providerData.vault_items.length} item{providerData.vault_items.length !== 1 ? 's' : ''} available
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {providerData.vault_items.length === 0 ? (
                      <div className="text-center py-8" data-testid={`empty-vault-${idx}`}>
                        <Vault className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-600">No items in vault yet</p>
                      </div>
                    ) : (
                      <Accordion type="single" collapsible className="space-y-2">
                        {Object.entries(groupedItems).map(([category, items]) => (
                          <AccordionItem key={category} value={category} className="border border-slate-200 rounded-lg px-4" data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}>
                            <AccordionTrigger className="hover:no-underline">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900">{category}</span>
                                <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                                  {items.length}
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-3 pt-2">
                                {items.map((item) => (
                                  <div
                                    key={item.id}
                                    className="bg-slate-50 rounded-lg p-4 border border-slate-200"
                                    data-testid={`access-item-${item.id}`}
                                  >
                                    <h4 className="font-semibold text-slate-900 mb-2">{item.title}</h4>
                                    <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3">{item.details}</p>
                                    {item.link && (
                                      <a
                                        href={item.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700"
                                        data-testid={`access-item-link-${item.id}`}
                                      >
                                        <ExternalLink className="w-3 h-3" />
                                        View Link
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}